import { motion } from "framer-motion";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Check, Quote } from "lucide-react";

const DESC_IMG = "/assets/gallery/conditioning.jpg";

const testimonials = [
  {
    quote: "At 71 I thought strength training wasn't for me. Six months in, I'm lifting confidently, gardening pain-free and my balance has completely changed.",
    name: "Margaret H.",
    role: "Strength Client, 71",
  },
  {
    quote: "After two years of back pain, Harry rebuilt how I move from the ground up. I'm stronger at 45 than I was at 30.",
    name: "Daniel K.",
    role: "Rehab Client",
  },
  {
    quote: "Added 9cm to my vertical in one off-season. The testing alone was worth it — everything after was just execution.",
    name: "Marcus T.",
    role: "Basketball, Semi-Pro",
  },
];

const plans = [
  {
    name: "1x Per Week",
    price: "£330",
    unit: "/month",
    tag: null,
    features: ["One 1-hour one-to-one session every week", "Fully personalised programme", "Week-by-week progress tracking", "WhatsApp coach support between sessions"],
  },
  {
    name: "2x Per Week",
    price: "£606",
    unit: "/month",
    tag: "Most Popular",
    features: ["Two 1-hour one-to-one sessions every week", "Fully personalised programme", "Faster progress with weekly adjustments", "Nutrition guidance", "WhatsApp coach support between sessions"],
  },
  {
    name: "Drop-In Session",
    price: "£80",
    unit: "/session",
    tag: null,
    features: ["A single 1-hour one-to-one session", "Perfect for trying the gym first", "Great for technique top-ups", "No commitment required"],
  },
  {
    name: "Group Sessions",
    price: "POA",
    unit: "",
    tag: null,
    features: ["Custom sessions for 2+ people", "Couples, friends, teams & workplaces", "Tailored to group size and goals", "Price on application — get in touch"],
  },
];

const reveal = {
  initial: { opacity: 0, y: 32 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
};

export default function CoreTabs() {
  return (
    <section id="programs" className="py-24 md:py-40 border-b border-white/10" data-testid="programs-section">
      <div className="max-w-[1600px] mx-auto px-5 md:px-10">
        <motion.h2
          {...reveal}
          transition={{ duration: 0.7 }}
          className="font-display font-bold uppercase leading-[0.9] text-5xl md:text-7xl lg:text-8xl mb-14 md:mb-20"
          data-testid="programs-heading"
        >
          Train <span className="text-stroke-gold">With</span> <span className="text-gold italic">Intent</span>
        </motion.h2>

        <Tabs defaultValue="description" className="w-full" data-testid="core-tabs">
          <TabsList className="w-full md:w-auto grid grid-cols-3 md:inline-flex bg-transparent border border-white/10 rounded-none h-auto p-0 mb-12">
            {[
              { value: "description", label: "Description" },
              { value: "testimonials", label: "Testimonials" },
              { value: "pricing", label: "Plans & Pricing" },
            ].map((t) => (
              <TabsTrigger
                key={t.value}
                value={t.value}
                data-testid={`tab-trigger-${t.value}`}
                className="rounded-none font-display uppercase tracking-[0.15em] text-sm md:text-base px-6 md:px-10 py-4 text-zinc-500 data-[state=active]:bg-gold data-[state=active]:text-black data-[state=active]:shadow-none transition-colors duration-300"
              >
                {t.label}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="description" data-testid="tab-content-description" className="mt-0">
            <motion.div {...reveal} transition={{ duration: 0.7 }} className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14 items-start">
              <div className="md:col-span-7">
                <p className="font-display uppercase tracking-[0.3em] text-xs text-gold mb-6">Who I Am</p>
                <h3 className="font-display font-semibold uppercase text-3xl md:text-5xl leading-[0.95] tracking-tight mb-8">
                  Serious strength coaching. For every body.
                </h3>
                <div className="space-y-5 text-zinc-400 text-base md:text-lg leading-relaxed max-w-2xl">
                  <p>
                    Walk in and you'll notice the difference straight away: a private, focused gym
                    in the heart of Northwood where every session is properly coached. You train
                    one-to-one with me — a coach who knows your name, your injuries and your
                    goals.
                  </p>
                  <p>
                    Your programme covers strength, rehabilitation, mobility and conditioning —
                    built around your body, your schedule and your target. Whether that's a
                    pain-free shoulder, your first pull-up, or a podium, I plan, track and adjust
                    the work week by week.
                  </p>
                </div>
              </div>
              <div className="md:col-span-5">
                <div className="clip-frame overflow-hidden bg-black">
                  <img src={DESC_IMG} alt="Concept2 rower, assault bike and plyo box in the conditioning area at OSHPerformance gym Northwood" className="w-full h-[380px] md:h-[520px] object-cover hover:scale-105 transition-transform duration-700" data-testid="description-image" />
                </div>
              </div>
            </motion.div>
          </TabsContent>

          <TabsContent value="testimonials" data-testid="tab-content-testimonials" className="mt-0">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {testimonials.map((t, i) => (
                <motion.figure
                  key={t.name}
                  {...reveal}
                  transition={{ duration: 0.6, delay: i * 0.12 }}
                  className="border border-white/10 p-8 md:p-10 flex flex-col justify-between bg-secondary/40 hover:border-gold/50 transition-colors duration-500"
                  data-testid={`testimonial-card-${i}`}
                >
                  <div>
                    <Quote className="w-7 h-7 text-gold mb-6" />
                    <blockquote className="font-display font-medium text-xl md:text-2xl leading-snug text-white">
                      "{t.quote}"
                    </blockquote>
                  </div>
                  <figcaption className="mt-8 pt-6 border-t border-white/10">
                    <p className="font-display uppercase tracking-[0.15em] text-gold">{t.name}</p>
                    <p className="text-zinc-500 text-sm mt-1">{t.role}</p>
                  </figcaption>
                </motion.figure>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="pricing" data-testid="tab-content-pricing" className="mt-0">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
              {plans.map((p, i) => (
                <motion.div
                  key={p.name}
                  {...reveal}
                  transition={{ duration: 0.6, delay: i * 0.12 }}
                  className={`relative flex flex-col p-8 md:p-10 border transition-colors duration-500 ${
                    p.tag ? "border-gold bg-secondary/60" : "border-white/10 bg-secondary/40 hover:border-white/30"
                  }`}
                  data-testid={`pricing-card-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {p.tag && (
                    <span className="absolute -top-3 left-8 bg-gold text-black font-display uppercase tracking-[0.2em] text-xs px-3 py-1" data-testid="pricing-badge">
                      {p.tag}
                    </span>
                  )}
                  <h3 className="font-display font-bold uppercase text-2xl md:text-3xl tracking-tight">{p.name}</h3>
                  <p className="mt-4 flex items-baseline gap-1">
                    <span className="font-display font-bold text-5xl md:text-6xl text-gold">{p.price}</span>
                    <span className="text-zinc-500 text-sm">{p.unit}</span>
                  </p>
                  <ul className="mt-8 space-y-3 flex-1">
                    {p.features.map((f) => (
                      <li key={f} className="flex items-start gap-3 text-zinc-300 text-sm md:text-base">
                        <Check className="w-4 h-4 text-gold mt-0.5 shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <a
                    href="#contact"
                    data-testid={`pricing-cta-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`mt-10 inline-flex justify-center font-display font-semibold uppercase tracking-[0.15em] px-6 py-3.5 transition-colors duration-300 ${
                      p.tag ? "bg-gold text-black hover:bg-gold-bright" : "border border-white/20 text-white hover:border-gold hover:text-gold"
                    }`}
                  >
                    {p.price === "POA" ? "Enquire Now" : `Choose ${p.name}`}
                  </a>
                </motion.div>
              ))}
            </div>
            <p className="text-zinc-500 text-sm mt-8" data-testid="pricing-note">
              All plans start with a free 30-minute consultation. No lock-in contracts.
            </p>
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
}
